import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Menu, X, Phone, MessageCircle, MapPin, LogIn, Package, ShoppingCart, User, LogOut } from 'lucide-react';
import { Button } from './ui/button';

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_0aec802e-e67b-4582-8fac-1517907b7262/artifacts/752tez4i_Logo%20Cucumaa%20Glass.png";

const DEFAULT_NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/products', label: 'Products' },
  { to: '/customize', label: 'Customize' },
  { to: '/industries', label: 'Industries' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/wholesale', label: 'Wholesale' },
  { to: '/contact', label: 'Contact' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [site, setSite] = useState(null);
  const { user, logout } = useAuth();
  const { getCartCount, setIsCartOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const cartCount = getCartCount();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/erp/cms/public/site-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(setSite)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const dashboardPath = user?.role === 'customer' ? '/portal' : '/erp/dashboard';

  const NAV_LINKS = (site?.header_menu?.length)
    ? site.header_menu.map(m => ({ to: m.url, label: m.title }))
    : DEFAULT_NAV_LINKS;

  const logoUrl = site?.logo_url || DEFAULT_LOGO;
  const phone = site?.phone || '9284701985';
  const whatsapp = site?.whatsapp || '919284701985';
  const locationText = [site?.city, site?.state].filter(Boolean).join(', ') || 'Pune, Maharashtra';

  return (
    <header className="sticky top-0 z-50">
      {/* ── Top info strip ── */}
      <div className="bg-slate-900 text-slate-400 text-xs hidden sm:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-8">
          <div className="flex items-center gap-5">
            <a href={`tel:+91${phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Phone className="w-3 h-3" />
              +91 {phone}
            </a>
            <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <MessageCircle className="w-3 h-3" />
              WhatsApp
            </a>
            <span className="hidden md:flex items-center gap-1.5">
              <MapPin className="w-3 h-3" />
              {locationText}
            </span>
          </div>
          <Link to="/track" className="flex items-center gap-1.5 hover:text-white transition-colors">
            <Package className="w-3 h-3" />
            Track Order
          </Link>
        </div>
      </div>

      {/* ── Main navigation bar ── */}
      <div className="bg-white/95 border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 min-w-0" data-testid="header-logo">
            <img src={logoUrl} alt="Lucumaa Glass" className="h-10 w-auto object-contain shrink-0" />
            <div className="hidden sm:block leading-tight min-w-0">
              <div className="text-base font-bold text-slate-900 truncate max-w-[200px]">{site?.site_title?.split(' - ')[0] || 'Lucumaa Glass'}</div>
              <div className="text-[10px] text-slate-500 truncate max-w-[200px]">{site?.site_tagline || 'Premium Glass Manufacturing'}</div>
            </div>
          </Link>

          {/* Desktop nav links */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                data-testid={label === 'Home' ? 'nav-home' : `nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive(to)
                    ? 'text-primary-700 bg-primary-50'
                    : 'text-slate-600 hover:text-primary-700 hover:bg-slate-50'
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* Right section: Cart · Auth · CTA */}
          <div className="flex items-center gap-2">
            {/* Cart */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative p-2 text-slate-500 hover:text-primary-700 hover:bg-slate-50 rounded-lg transition-all"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold leading-none px-1">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </button>

            {/* Auth – desktop */}
            {user ? (
              <div className="hidden lg:flex items-center gap-1">
                <Link
                  to={dashboardPath}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  data-testid="header-dashboard-link"
                >
                  <User className="w-4 h-4" />
                  {user.name?.split(' ')[0] || 'Dashboard'}
                </Link>
                <button
                  onClick={handleLogout}
                  className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid="header-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Button
                asChild
                className="hidden lg:flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 text-white h-9 px-4 text-sm font-semibold rounded-lg shadow-sm transition-colors"
                data-testid="header-login-link"
              >
                <Link to="/login">
                  <LogIn className="w-4 h-4" />
                  Login / Register
                </Link>
              </Button>
            )}

            {/* CTA – desktop */}
            <Button
              asChild
              className="hidden lg:flex bg-primary-700 hover:bg-primary-800 text-white h-9 px-4 text-sm font-semibold rounded-lg shadow-sm"
              data-testid="nav-get-quote-btn"
            >
              <Link to="/customize">Get Quote</Link>
            </Button>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-700 hover:bg-slate-100 rounded-lg"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile slide-in drawer ── */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Drawer */}
          <div className="absolute inset-y-0 right-0 w-80 max-w-[85vw] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <img src={logoUrl} alt="" className="h-8 w-auto object-contain" />
                <span className="font-bold text-slate-900 text-sm">Menu</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-1.5 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {/* Auth card */}
              {user ? (
                <div className="mb-5 p-4 bg-primary-50 rounded-xl border border-primary-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{user.name}</div>
                      <div className="text-xs text-slate-500 capitalize">{user.role?.replace('_', ' ')}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Link
                      to={dashboardPath}
                      className="flex-1 text-center px-3 py-2 bg-primary-600 text-white text-sm font-semibold rounded-lg"
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-5 flex gap-2">
                  <Link
                    to="/login"
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-3 bg-primary-600 text-white text-sm font-semibold rounded-xl shadow-sm"
                  >
                    <LogIn className="w-4 h-4" />
                    Login
                  </Link>
                  <Link
                    to="/login"
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-3 border-2 border-primary-200 text-primary-700 text-sm font-semibold rounded-xl"
                  >
                    Register
                  </Link>
                </div>
              )}

              {/* Navigation links */}
              <div className="space-y-1">
                {NAV_LINKS.map(({ to, label }) => (
                  <Link
                    key={to}
                    to={to}
                    className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive(to)
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {label}
                  </Link>
                ))}
              </div>

              {/* Quick actions */}
              <div className="mt-5 pt-5 border-t border-slate-100 space-y-3">
                <Link
                  to="/track"
                  className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg"
                >
                  <Package className="w-4 h-4 text-primary-600" />
                  Track Order
                </Link>
                <a
                  href={`tel:+91${phone}`}
                  className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg"
                >
                  <Phone className="w-4 h-4 text-primary-600" />
                  +91 {phone}
                </a>
                <a
                  href={`https://wa.me/${whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg"
                >
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  WhatsApp Us
                </a>
              </div>
            </div>

            {/* Drawer footer CTA */}
            <div className="px-5 py-4 border-t border-slate-100">
              <Button asChild className="w-full bg-primary-700 hover:bg-primary-800 text-white h-11 text-sm font-semibold rounded-xl">
                <Link to="/customize">Get Free Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
